import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function RouteProgressBar() {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const prevPath = useRef(location.pathname);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (location.pathname === prevPath.current) return;
    prevPath.current = location.pathname;

    // Start progress
    setProgress(0);
    setVisible(true);

    // Simulate fast load: ramp quickly then slow down
    let current = 0;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      current += current < 60 ? 12 : current < 85 ? 4 : 0.5;
      if (current >= 92) {
        current = 92;
        clearInterval(timerRef.current);
      }
      setProgress(current);
    }, 40);

    // Complete after a short delay (matches page transition duration)
    const completeTimer = setTimeout(() => {
      clearInterval(timerRef.current);
      setProgress(100);
      setTimeout(() => setVisible(false), 200);
    }, 350);

    return () => {
      clearInterval(timerRef.current);
      clearTimeout(completeTimer);
    };
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed top-0 left-0 right-0 z-[9999] h-[2px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="h-full rounded-r-full"
            style={{
              width: `${progress}%`,
              background: "linear-gradient(90deg, hsl(25 85% 50%), hsl(33 90% 55%))",
              boxShadow: "0 0 8px hsl(25 85% 50% / 0.5), 0 0 20px hsl(25 85% 50% / 0.2)",
            }}
            transition={{ duration: 0.15, ease: "easeOut" }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
